import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import { toFilters } from '@/api/customers';
import type { ResponseArray, ResponseObject } from '@/types/shared';
import type { ContractListParams } from '@/api/contracts';

/**
 * Contract Samples — sample/template contracts (`GET /contracts/samples`).
 * Shapes verified against live api-labs-datacentral 2026-08-27.
 * Endpoint: /api/v4/contracts/samples  (GET list + GET mapping)
 * Fields: contract_id, contract_number_id, contract_note, contract_status,
 *   contract_effective_date, contract_expiry_date, contract_value,
 *   employee_id, created_at, sla_enable, nda_enable, product_group_name,
 *   updated_at, contract_key, employee_name, attachments[]
 */

/* ── Types ──────────────────────────────────────────────────────────────── */

/** Attachment embedded on a sample contract row. */
export interface SampleContractAttachment {
  attachment_id: number;
  attachment_file_name: string;
  attachment_file_type: string;
  attachment_file_url: string;
  attachment_file_size: number;
  attachment_closed: boolean;
  thumbnail: string;
}

/** `GET /contracts/samples` list row — verified against live API 2026-08-27. */
export interface SampleContractRow {
  contract_id: number;
  contract_number_id: string;
  contract_note: string | null;
  contract_status: string;
  contract_effective_date: string | null;
  contract_expiry_date: string | null;
  contract_value: number | null;
  employee_id: number | null;
  created_at: string;
  sla_enable: boolean;
  nda_enable: boolean;
  product_group_name: string;
  updated_at: string;
  contract_key: string | null;
  employee_name: string | null;
  attachments: SampleContractAttachment[];
}

/** `GET /contracts/samples/mapping` item — { label, value } pairs. */
export interface SampleMappingOption {
  label: string;
  value: string;
}

/**
 * Payload for `POST /contracts/samples`.
 * Matches `RequestCreateSampleContractParams` (contracts.samples.types.go).
 * Inner `contract` object maps to `db.CreateSampleContractParams`.
 */
export interface SampleContractCreatePayload {
  contract: {
    /** Optional short key / alias for this sample. */
    contract_key?: string;
    /** Display ID (e.g. "MHD-2026-001"). Required by the backend `contract_number_id` NOT NULL. */
    contract_number_id: string;
    contract_note?: string;
    /** e.g. `"active"` */
    contract_status?: string;
    /** `YYYY-MM-DD` */
    contract_effective_date?: string;
    /** `YYYY-MM-DD` */
    contract_expiry_date?: string;
    contract_value?: number;
    sla_enable?: boolean;
    nda_enable?: boolean;
    /** Product group code, e.g. `"CDN"` — drives the template. */
    product_group_name: string;
  };
  attachments?: Array<{
    attachment_file_name: string;
    attachment_file_url: string;
    attachment_file_type?: string;
    attachment_file_size?: number;
  }>;
}

/**
 * Payload for `PUT /contracts/samples/:sampleContractID`.
 * Matches `RequestUpdateSampleContractParams` (contracts.samples.types.go).
 */
export interface SampleContractUpdatePayload {
  contract: {
    contract_number_id: string;
    contract_note?: string;
    contract_status?: string;
    contract_key?: string;
    contract_effective_date?: string;
    contract_expiry_date?: string;
    contract_value?: number;
    sla_enable?: boolean;
    nda_enable?: boolean;
    product_group_name: string;
  };
  attachments?: Array<{
    attachment_file_name: string;
    attachment_file_url: string;
    attachment_file_type?: string;
    attachment_file_size?: number;
  }>;
  /** IDs of attachments to remove. */
  attachments_to_delete?: number[];
}

/* ── Query keys ─────────────────────────────────────────────────────────── */

export const sampleContractKeys = {
  all: ['contracts', 'samples'] as const,
  list: (params: ContractListParams) => ['contracts', 'samples', 'list', params] as const,
  mapping: () => ['contracts', 'samples', 'mapping'] as const,
};

/* ── Query helpers ──────────────────────────────────────────────────────── */

function listQuery(params: ContractListParams): string {
  const { filters, ...rest } = params;
  const q: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (v !== undefined && v !== '') q[k] = v as string | number;
  }
  if (filters && Object.keys(filters).length > 0) q.filters = toFilters(filters);
  return qs.stringify(q);
}

/* ── Hooks ──────────────────────────────────────────────────────────────── */

/** `GET /contracts/samples` — paginated sample contract list. */
export function useSampleContracts(params: ContractListParams = {}) {
  return useQuery({
    queryKey: sampleContractKeys.list(params),
    queryFn: async (): Promise<ResponseArray<SampleContractRow>> => {
      const res = await dataCentralApi.get<ResponseArray<SampleContractRow>>(
        `/contracts/samples?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /contracts/samples/mapping` — product group name options. */
export function useSampleContractMapping() {
  return useQuery({
    queryKey: sampleContractKeys.mapping(),
    queryFn: async (): Promise<SampleMappingOption[]> => {
      const res = await dataCentralApi.get<ResponseArray<SampleMappingOption>>(
        '/contracts/samples/mapping',
      );
      return res.data?.results ?? [];
    },
    staleTime: 5 * 60 * 1000, // mapping rarely changes
  });
}

/* ── Mutation hooks ─────────────────────────────────────────────────────── */

/** `POST /contracts/samples` — create a sample contract. */
export function useCreateSampleContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: SampleContractCreatePayload,
    ): Promise<ResponseObject<SampleContractRow>> => {
      const res = await dataCentralApi.post<ResponseObject<SampleContractRow>>(
        '/contracts/samples',
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sampleContractKeys.all });
    },
  });
}

/**
 * `PUT /contracts/samples/:sampleContractID` — update a sample contract.
 * DELETE verb is not confirmed in api-v4 routes — deferred.
 */
export function useUpdateSampleContract(sampleContractId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: SampleContractUpdatePayload,
    ): Promise<ResponseObject<SampleContractRow>> => {
      const res = await dataCentralApi.put<ResponseObject<SampleContractRow>>(
        `/contracts/samples/${sampleContractId}`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sampleContractKeys.all });
    },
  });
}
